/**
 * DI-3-C — the runner's state (Zustand, ephemeral UI state per the repo's
 * state rule; every transition is ALSO written to the session record in
 * IndexedDB by the runner, which is what survives a reload).
 *
 * One entry per data set that was started: its frozen membership (row indexes
 * of THIS tab), the create it sends, its phase and phase times, the last
 * error, and what the server answered. The page and (DI-3-D) the board
 * subscribe; neither owns it.
 */
import { createStore, useStore, type StoreApi } from "zustand";
import { uploadPending } from "./uploadPending.js";
import type { DataAcquisitionCreated } from "../api/types.js";
import type { RoundTripResult } from "../sync/roundTrip.js";
import type {
  CreatePlan,
  ImageUploadMode,
  MetadataCompleteRecord,
  UploadChoice,
} from "../session/sessionStore.js";
import type { PhaseTimes, SetError, SetPhase } from "./phases.js";
import type { AutoRetry } from "./autoRetry.js";
import type { SetUpload, SetWithhold } from "../upload/state.js";

export interface RunnerSet {
  clusterId: string;
  ordinal: number;
  /** Row indexes of this tab's index (empty after a reload until the folder is read again). */
  memberIndexes: number[];
  imageCount: number;
  sizeBytes: number;
  centroid: { lat: number; lon: number };
  plan: CreatePlan;
  /** Display: the turbine / instruction as the row showed them at Start. */
  turbineLabel: string;
  instructionLabel: string;
  uploadChoice: UploadChoice | null;
  phase: SetPhase;
  phaseTimes: PhaseTimes;
  error: SetError | null;
  /** DI-3-S68 B2 — the automatic retry's ladder (persisted); null / absent = none. */
  autoRetry?: AutoRetry | null;
  created?: DataAcquisitionCreated;
  roundTrip?: RoundTripResult;
  /**
   * DI-3-S68 F6 — the files a set stopped on are read again ("Reading
   * again… n / m"); null / absent otherwise. Never persisted.
   */
  reread?: { done: number; total: number } | null;
  /** In-set duplicates dropped / rows without a hash, at send time. */
  sendStats?: { duplicates: number; unhashed: number };
  metadataComplete?: MetadataCompleteRecord;
  /** DI-3-D — the round trip's progress (per batch; persisted). */
  syncProgress?: { sent: number; total: number };
  /** DI-3-F — the originals' upload (uploading workflows only; persisted). */
  upload?: SetUpload;
  /** DI-3-F — a partner set's withhold (persisted). */
  withhold?: SetWithhold;
  /**
   * DI-3-G — "Add images…": the set is bound to this EXISTING acquisition (no
   * create — `created` is the acquisition itself); its round trip sends every
   * file of the folder, its upload only the new ones (`uploadKeys`).
   */
  boundTo?: string;
  /** DI-3-G — the new files' keys (`add/diff.ts:fileKey`); absent = every member. */
  uploadKeys?: string[];
  /** M5-NODE-01 — the version's image upload mode at Start (persisted). */
  imageUpload?: ImageUploadMode;
  /** M5-NODE-01 — the escape hatch's reason, sent on every session open (persisted). */
  uploadReason?: string;
}

export interface RunnerState {
  sessionId: string | null;
  folderName: string | null;
  /** True once Start was pressed for the current drop. */
  started: boolean;
  startedAt: string | null;
  sets: RunnerSet[];
  /** True while the runner works through its queue. */
  busy: boolean;
  /** DI-3-D — when this tab started reading the folder (the hash rate's clock). */
  indexStartedAt?: string | null;
  /** DI-3-F — "Pause uploads" is on (the tab-wide queue grants no new PUT). */
  uploadsPaused?: boolean;
  /**
   * DI-3-P2 — the last `reattach` (a resume): how many files the folder gave
   * and how many of them the worker was handed a persisted hash for (their
   * bytes are not read again). Null outside a resume.
   */
  reattached?: ReattachCounts | null;
}

/** DI-3-P2 — what a resume's re-index reads, and what it keeps. */
export interface ReattachCounts {
  files: number;
  hashesKept: number;
}

export const EMPTY_RUNNER: Omit<RunnerState, never> = {
  sessionId: null,
  folderName: null,
  started: false,
  startedAt: null,
  sets: [],
  busy: false,
  indexStartedAt: null,
  uploadsPaused: false,
  reattached: null,
};

export function createRunnerStore(): StoreApi<RunnerState> {
  return createStore<RunnerState>()(() => ({ ...EMPTY_RUNNER }));
}

/** Replace one set by `clusterId`. */
export function patchSet(
  store: StoreApi<RunnerState>,
  clusterId: string,
  patch: Partial<RunnerSet>,
): void {
  store.setState((s) => ({
    sets: s.sets.map((x) =>
      x.clusterId === clusterId ? { ...x, ...patch } : x,
    ),
  }));
}

export function useRunnerState<T>(
  store: StoreApi<RunnerState>,
  selector: (s: RunnerState) => T,
): T {
  return useStore(store, selector);
}

/** Every started set has reached `running` or stopped on an error. */
export function allHandedOver(sets: readonly RunnerSet[]): boolean {
  return (
    sets.length > 0 &&
    sets.every((s) => s.phase === "running" || s.error !== null)
  );
}

/**
 * DI-3-D (N8) — does this tab hold browser work that closing it would pause?
 * A started set that has not reached `running` and is not stopped on an
 * error still needs this tab (create, hash, metadata), and — DI-3-F — so does
 * an upload that may run and has bytes or a manifest to send (queued,
 * uploading, committing; the poll of a completed manifest needs no tab: the
 * server verifies and promotes by itself). DI-5-1 — and a client node
 * running after the approve (the write-back writing the images on this PC,
 * or its completion being recorded): `clientNodes` = the client-node
 * runner's answer (`runner/clientNodes.ts` `clientNodeWorkPending`). The
 * guards (`guards.ts`) and the board read it.
 */
export function hasPendingLocalWork(
  s: Pick<RunnerState, "started" | "sets">,
  clientNodes = false,
): boolean {
  if (clientNodes) return true;
  return (
    s.started &&
    s.sets.some(
      (x) => (x.phase !== "running" && x.error === null) || uploadPending(x),
    )
  );
}
